<!-- Home.svelte -->
<script>
  import { user } from '$lib/stores/store';
  import { onMount, onDestroy } from 'svelte';
  import * as Lucide from 'lucide-svelte';
  import Modal from '@components/ui/modal/Modal.svelte';

  import SettingsPage from '@components/settings/SettingsPage.svelte';
  import { hotkeys } from '$lib/stores/hotkeyStore.js';
  import Splotlight from '@components/Spotlight.svelte';
  import { faceStore } from '$lib/stores/face-store';
  import IframeWrapper from '@components/IframeWrapper.svelte';
  import { menus } from '$lib/stores/store';
  import PinDialog from '@components/security/PinDialog.svelte';
  import ApiClient from '$lib/api';
  import { notify } from '@components/toast';

  $: currentMenu = $menus.find((x) => x.id === $selectedFace);

  $: if (currentMenu) {
    menuVisible.set(!currentMenu.collapseMenu);
  }

  $: CurrentComponent = currentMenu?.componentRef;

  const api = new ApiClient();

  let open = false;
  let openPinDialog = false;
  let settingsBypass = 0;
  let openingSettings = false;
  let settingsPageRef;
  let settingsModalDirty = false;
  let settingsModalBusy = false;
  let modalOption = { width: '80%', height: '80%', title: '' };
  const { face: selectedFace, menuVisible, changeFace } = faceStore;

  onMount(() => {
    window.addEventListener('keydown', hotkeys.trigger);

    $menus.forEach((menu) => {
      if (!menu.hotkey) return;

      hotkeys.register(menu.hotkey.toLowerCase(), () => {
        changeFace(menu.id);
      });
    });
  });

  function normalizeBypassMs(value) {
    const next = Number(value);

    if (!Number.isFinite(next)) return 1000;

    return Math.min(60000, Math.max(500, Math.round(next)));
  }

  async function openSettings() {
    if (openingSettings) return;

    try {
      openingSettings = true;
      settingsBypass = 0;

      const userId = $user?.id;

      if (userId) {
        const rs = await api.get(`/api/user-security/${userId}`);

        if (rs?.success) {
          settingsBypass = rs.hasPin ? 0 : normalizeBypassMs(rs.bypassMilliseconds);
        }
      }

      openPinDialog = true;
    } catch (err) {
      notify.error(err?.message || 'Unable to load security settings');
      openPinDialog = true;
    } finally {
      openingSettings = false;
    }
  }

  function handleSettingsState(event) {
    settingsModalDirty = Boolean(event.detail?.dirty);
    settingsModalBusy = Boolean(event.detail?.busy);
  }

  async function confirmSettingsModal() {
    if (settingsModalBusy) return;

    const shouldClose = await settingsPageRef?.confirmModal?.();
    if (shouldClose !== false) {
      open = false;
    }
  }

  function closeSettingsModal() {
    const canClose = settingsPageRef?.canCloseModal?.() ?? true;
    if (canClose !== false) {
      open = false;
    }
  }

  onDestroy(() => {
    window.removeEventListener('keydown', hotkeys.trigger);

    $menus.forEach((menu) => {
      if (!menu.hotkey) return;

      hotkeys.unregister(menu.hotkey.toLowerCase());
    });
  });
</script>

<div class="h-screen flex overflow-hidden">
  <div class="bg-gray-200 transition-all duration-300" class:w-[50px]={$menuVisible} class:w-0={!$menuVisible}>
    <div
      class="menu-left h-full w-full bg-blue-100 border-r-[2px] border-r-gray-300 flex flex-col items-center justify-between py-4 overflow-hidden">
      <!-- Menu Top -->
      <div class="flex flex-col border-b border-gray-300 gap-y-5 pb-5">
        {#each $menus.filter((x) => x.visible) as menu}
          <button
            class="w-11 h-11 flex items-center justify-center rounded-full
             bg-white
             border border-slate-200
             text-slate-600
             shadow-sm
             hover:bg-blue-50
             hover:border-blue-300
             hover:text-blue-600
             transition-all duration-200"
            on:click={() => changeFace(menu.id)}
            title={menu.label}>
            <svelte:component this={Lucide[menu.icon] || Lucide.CircleQuestionMark} size={28} />
          </button>
        {/each}
      </div>

      <!-- Menu Bottom -->
      <div class="flex flex-col items-center">
        <button
          class="w-11 h-11 flex items-center justify-center rounded-full
             bg-white
             border border-slate-200
             text-slate-600
             shadow-sm
             hover:bg-blue-50
             hover:border-blue-300
             hover:text-blue-600
             transition-all duration-200"
          disabled={openingSettings}
          on:click={openSettings}>
          <Lucide.Settings size={28} />
        </button>
      </div>
    </div>
  </div>

  <!-- Nội dung -->
  <div class="flex-1 overflow-hidden bg-white">
    {#if currentMenu?.type === 'iframe'}
      <IframeWrapper src={currentMenu.src} title={currentMenu.label} showToolbar={currentMenu.toolbar !== false} />
    {:else if CurrentComponent}
      <svelte:component this={CurrentComponent} {user} onClose={() => changeFace('info')} />
    {:else}
      <div class="p-4">Module not found</div>
    {/if}
  </div>
</div>

<!-- PinDialog -->
<PinDialog
  bind:open={openPinDialog}
  bypass={settingsBypass}
  onSuccess={() => {
    open = true;
    settingsModalDirty = false;
    settingsModalBusy = false;

    modalOption = {
      width: '80%',
      height: '80%',
      title: 'Cài đặt'
    };
  }} />

<!-- Modal -->
<Modal
  {...modalOption}
  bind:open
  closeOnOutsideClick={false}
  onClose={closeSettingsModal}
  onConfirm={confirmSettingsModal}
  confirmDisabled={settingsModalBusy}
  confirmBusy={settingsModalBusy}
  confirmText={settingsModalDirty ? 'Save and close' : 'OK'}
  cancelText={settingsModalDirty ? 'Discard' : 'Cancel'}>
  <SettingsPage bind:this={settingsPageRef} on:modalstate={handleSettingsState} />
</Modal>

<Splotlight
  items={$menus.filter((x) => x.visible)}
  onSelect={(item) => {
    changeFace(item.id);
  }} />
